import { btnGhost } from "./styles";

// Page numbers to show: first, last, and a window around the current page.
const pageWindow = (current: number, last: number): (number | "…")[] => {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const pages = new Set<number>([1, last, current - 1, current, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= last)
    .sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push("…");
    out.push(p);
  });
  return out;
};

export const PER_PAGE_OPTIONS = [25, 50, 100, 250];

const Pagination = ({
  currentPage,
  lastPage,
  total,
  onPageChange,
  perPage,
  onPerPageChange,
  perPageOptions = PER_PAGE_OPTIONS,
  pageSize,
}: {
  currentPage: number;
  lastPage: number;
  total?: number;
  onPageChange: (page: number) => void;
  /** optional: shows a "Rows per page" picker and a "1–25 of 1,234" range */
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  perPageOptions?: number[];
  /** rows the API actually returned per page, if it differs from perPage */
  pageSize?: number;
}) => {
  if (lastPage < 1) return null;

  const withPicker = perPage !== undefined && !!onPerPageChange;
  const size = pageSize ?? perPage ?? 0;
  const from = withPicker && total ? (currentPage - 1) * size + 1 : 0;
  const to = withPicker && total ? Math.min(currentPage * size, total) : 0;

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 px-5 pt-4 border-t border-hcBorder">
      {withPicker ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-hcMuted">
          <label className="flex items-center gap-2">
            Rows per page
            <select
              value={perPage}
              onChange={(e) => onPerPageChange!(Number(e.target.value))}
              className="rounded-lg border border-hcBorder bg-white py-1.5 pl-2.5 pr-7 text-sm font-medium text-honeConnectBlack focus:border-honeConnectPurple focus:outline-none focus:ring-2 focus:ring-honeConnectPurple/30"
            >
              {perPageOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {typeof total === "number" && total > 0 && (
            <p>
              <span className="font-medium text-honeConnectBlack">
                {from.toLocaleString()}–{to.toLocaleString()}
              </span>{" "}
              of {total.toLocaleString()}
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-hcMuted">
          Page <span className="font-medium text-honeConnectBlack">{currentPage}</span>{" "}
          of <span className="font-medium text-honeConnectBlack">{lastPage}</span>
          {typeof total === "number" && <> · {total.toLocaleString()} results</>}
        </p>
      )}

      {lastPage > 1 && (
      <div className="flex items-center gap-1.5">
        <button
          className={btnGhost}
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Previous
        </button>

        {pageWindow(currentPage, lastPage).map((p, i) =>
          p === "…" ? (
            <span key={`gap-${i}`} className="px-1 text-hcMuted">
              …
            </span>
          ) : (
            <button
              key={p}
              title={`page ${p}`}
              onClick={() => onPageChange(p)}
              className={`min-w-[36px] py-2 rounded-lg text-sm font-medium border transition ${
                p === currentPage
                  ? "bg-honeConnectPurple text-white border-honeConnectPurple"
                  : "bg-white text-honeConnectGray border-hcBorder hover:bg-hcBg"
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          className={btnGhost}
          disabled={currentPage >= lastPage}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
        </button>
      </div>
      )}
    </div>
  );
};

export default Pagination;
